import { CommonModule, NgFor, NgForOf, NgIf } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/services/toast.service';
import { take, tap } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { WindowService } from 'src/app/services/window.service';
import { SessionService } from 'src/app/services/session.service';
import { groupBy, mapValues, filter } from 'lodash';
import { Location } from '@angular/common';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import { ActiveSessionModel } from 'src/app/models/active-session.model';
import { ApiService } from 'src/app/services/api.service';
import { ContactGroup, SessionDetails, SessionImageModel } from 'src/app/models/session-page.model';
import { OnlineClientService } from 'src/app/services/online-client.service';
import { AutologinForSessionTakeoverResponse, AutologinModel } from 'src/app/models/autologin.model';
import * as UAParser from 'ua-parser-js';

@Component({
  selector: 'tx-session-page',
  standalone: true,
  imports: [NgIf, NgFor, NgForOf, NgbPopover, CommonModule],
  templateUrl: './session-page.component.html',
  styleUrl: './session-page.component.scss',
})
export class SessionPageComponent implements OnInit, OnDestroy {
  public sessions: any[] = [];
  public closeEnabled: boolean = false;
  public submitUrl: string;
  public sessionId = SESSION_ID;
  public groupedSessions: any[] = [];
  public loading = false;
  public loadingPreview = true;
  public savedPreviews: any = {};
  public previewImage: Blob | undefined;
  private getSessionsInterval: any;
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;

  constructor(
    private sessionService: SessionService,
    private apiService: ApiService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private windowService: WindowService,
    private location: Location,
    private onlineClientService: OnlineClientService
  ) {
    this.submitUrl = environment.endpoints.apiUrl + '/session/TakeOverSessionInNewTab';
  }

  ngOnInit(): void {
    let currentUrl = this.location.path().split('?')[0];
    this.location.replaceState(currentUrl);
    this.getSessions();
    this.getSessionsInterval = setInterval(this.getSessions.bind(this), 5000);
  }

  ngOnDestroy(): void {
    if (this.getSessionsInterval) {
      clearInterval(this.getSessionsInterval);
    }
  }

  onSessionSelected(session: any) {}

  onTakeoverClicked(session: ActiveSessionModel, caseSessionDetails: SessionDetails) {
    // check for embedded browser
    // in this case we need to create an autologin and send a url with cookie through the CEQuery function
    // otherwise the editor takes over the session in a new tab the regular way
    if (typeof window.CEQuery === 'function') {
      session.filename = caseSessionDetails.contactCaseDetails.filename;

      this.sessionService
        .createAutologinForSessionTakeover(session)
        .pipe(take(1))
        .subscribe((a: AutologinModel) => {
          if (a) {
              window.CEQuery('TAKEOVEREDITORSESSION', a.url, a.cookieName, a.cookieValue, a.filename);
          }
        });
    } else {
      this.takeoverSession(session);
    }
  }

  onSaveClicked(session: ActiveSessionModel) {
    this.sessionService
      .saveDocumentAndcloseSession(session)
      .pipe(take(1))
      .subscribe((res) => {
        this.getSessions();
      });
  }

  public getSessionThumbnail(session: ActiveSessionModel): any {
    if (this.savedPreviews[session.txTextControlSessionId] != undefined) {
      this.previewImage = this.savedPreviews[session.sessionId];
      this.loadingPreview = false;
    }
    return this.sessionService.getSessionThumbnail(session).subscribe((blob) => {
      this.savedPreviews[session.txTextControlSessionId] = URL.createObjectURL(blob);
      this.previewImage = this.savedPreviews[session.txTextControlSessionId];
      this.loadingPreview = false;
    });
  }

  public clearSessionThumbnail() {
    this.previewImage = undefined;
    this.loadingPreview = true;
  }

  private takeoverSession(session: ActiveSessionModel) {
    const filenameInput = document.getElementById('filename') as HTMLInputElement;
    if (filenameInput) {
      filenameInput.value = session?.activeDcmPath ?? '';
    }

    const activeSessionId = document.getElementById('activeSessionId') as HTMLInputElement;
    if (activeSessionId) {
      activeSessionId.value = session?.sessionId ?? '';
    }

    const activeSessionAnswerPoolId = document.getElementById('activeSessionAnswerPoolId') as HTMLInputElement;
    if (activeSessionAnswerPoolId) {
      activeSessionAnswerPoolId.value = session?.answerPoolId ?? '';
    }

    // current sessionId for authentication
    const sessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
    if (sessionId) {
      sessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
    }

    this.openInNewTabForm.nativeElement.submit();
  }

  onCloseClicked(session: ActiveSessionModel) {
    return this.dialogService.confirm({
      title: 'Discard Changes',
      message: 'Are you sure you want to discard and close that session? Any unsaved changes will be lost.',
      confirmButtonText: 'Discard',
      rejectButtonText: 'Cancel',
      size: 'sm',
      confirmCallback: () => {
        this.closeSession(session);
      },
      rejectCallback: () => {},
    });
  }

  closeSession(session: ActiveSessionModel) {
    this.sessionService
      .closeSession(session)
      .pipe(take(1))
      .subscribe((res) => {
        this.getSessions();
      });
  }

  getDocumentName(session: SessionDetails): string {
    let fileName = 'Untitled Document';
    if (session.session.filename != null) {
      fileName = session.session.filename;
    } else if (session.contactCaseDetails.filename != null) {
      fileName = session.contactCaseDetails.filename;
    }

    return fileName;
  }

  getDocumentNameTooltip(session: SessionDetails) {
    let tooltip = this.getDocumentName(session);

    if (this.isDirty(session.session)) {
      tooltip += '. This document has unsaved changes.';
    }
    return tooltip;
  }

  public isDirty(session: ActiveSessionModel): boolean {
    return session.dateAutosaved != null;
  }

  public getIconExtension(filename: string): string {
    if (!filename) {
      return 'file-earmark';
    }
    const ext = splitFilename(filename.toLowerCase())?.extension;
    if (ext) {
      return 'filetype-' + ext;
    }
    return 'file-earmark';
  }

  public isPdf(filename: string): boolean {
    if (!filename) return false;
    return splitFilename(filename.toLowerCase())?.extension === 'pdf';
  }

  getUserAgentName(userAgent: string): string {
    let parser = new UAParser(userAgent);
    const result = parser.getResult();
  
    return `${result.browser.name} on ${result.os.name} ${result.os.version}`;
  }

  private getSessions() {
    this.loading = true;
    this.sessionService
      .getFirmSessions()
      .pipe(
        take(1),
        tap((res: any) => {
          this.sessions = res.sessions;
          const validSessions = filter(this.sessions, (session) => session.contactCaseDetails);

          // Step 2: Group by ctcID
          const groupedByCtcID = groupBy(validSessions, (session) => session.contactCaseDetails?.ctcID);

          // Step 3: Further group by casID within each ctcID group
          const groupedByCasID = mapValues(groupedByCtcID, (sessionsGroup) => {
            return groupBy(sessionsGroup, (session) => session.contactCaseDetails?.casID);
          });

          // step 4: Put the groups in models that will be able to be displayed in the HTML
          this.groupedSessions = [];
          for (const contactKey in groupedByCasID) {
            if (groupedByCasID.hasOwnProperty(contactKey)) {
              const caseSessions = groupedByCasID[contactKey];

              // Extract firstName and lastName from the first session in the first case (assuming it's consistent)
              const firstSession = caseSessions[Object.keys(caseSessions)[0]][0];
              const firstName = firstSession.contactCaseDetails.firstName;
              const lastName = firstSession.contactCaseDetails.lastName;

              const grouped: ContactGroup = {
                contactKey,
                firstName,
                lastName,
                cases: [],
              };

              for (const caseId in caseSessions) {
                if (caseSessions.hasOwnProperty(caseId)) {
                  const sessions: SessionDetails[] = caseSessions[caseId];
                  grouped.cases.push({
                    caseId,
                    sessions,
                  });
                }
              }

              this.groupedSessions.push(grouped);
            }
          }
        }),
        tap((res: any) => {
          this.loading = false;
        })
      )
      .subscribe();
  }
}
